/** @jsxImportSource @emotion/react */

import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { css } from '@emotion/react';
import { Dialog, DialogActions, DialogContent, Divider } from '@mui/material';
import { QSButton, QSTypography } from '../../../../../shared/components/atoms';
import Loader from '../../../../../shared/components/molecules/loader';
import useNotifications from '../../../../../shared/hooks/use-notifications';
import useQuery from '../../../../../shared/hooks/useQuery';
import { PlanAddonTypes } from '../../../../renew-plan/v1/typings';
import { FreeTrialStep, formatBillingPeriod } from './typings';
import { freeTrialStepAtom, startTrialAtom, trialDetailsAtom } from './states';
import { getTrialDetails, startTrial } from './requests';
import CallAllowIcon from '../../../../../shared/icons/icons-revamp/call_allow';
import ErrorContainer from '../../../../../shared/components/molecules/error';
import { getCurrencySymbol } from '../../../../../shared/utils/currency-symbol';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(advancedFormat);

export default function TrialDetailsModal() {
  const [step, setStep] = useAtom(freeTrialStepAtom);
  const [startTrialData, setStartTrialData] = useAtom(startTrialAtom);
  const { addNotification } = useNotifications();

  const queryFn = () => getTrialDetails(PlanAddonTypes.CallingLicense);
  const { requestData: trialDetails } = useQuery({
    requestAtom: trialDetailsAtom,
    queryFunction: queryFn,
    getCachedData: true,
  });

  const details = trialDetails.data;

  const trialStartDate = new Date();
  const trialEndDate = details
    ? new Date(trialStartDate.getTime() + details.numberOfTrialDays * 24 * 60 * 60 * 1000)
    : null;

  const billingDate = trialEndDate ? new Date(trialEndDate) : null;

  const onClose = useCallback(() => {
    setStep(FreeTrialStep.Closed);
  }, [setStep]);

  const onStartTrial = useCallback(async () => {
    if (!details) return;
    setStartTrialData((prev) => ({ ...prev, loading: true }));
    try {
      const result = await startTrial(PlanAddonTypes.CallingLicense);
      setStartTrialData({ loading: false, refreshing: false, data: result });
      setStep(FreeTrialStep.GrantAccess);
    } catch (error) {
      setStartTrialData((prev) => ({ ...prev, loading: false }));
      addNotification({
        message: (error as Error).message ?? 'Failed to start trial. Please try again.',
        type: 'error',
      });
    }
  }, [details, setStartTrialData, setStep, addNotification]);

  const currencySymbol = details?.currencyCode ? getCurrencySymbol(details.currencyCode) : '';
  const priceDisplay = details?.amountPerUnit ?? 0;
  const billingPeriodLabel = details
    ? formatBillingPeriod(details.period, details.periodUnit)
    : 'per license per month';

  return (
    <Dialog
      open={step === FreeTrialStep.TrialDetails}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          width: '550px',
          maxWidth: '100%',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        css={css`
          padding: 24px 24px 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        `}
      >
        {trialDetails.loading ? (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 12px;
              padding: 24px 0;
              width: 100%;
            `}
          >
            <Loader size={28} />
          </div>
        ) : trialDetails.error ? (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100%;
            `}
          >
            <ErrorContainer error={trialDetails.error?.message || 'Something went wrong'} />
          </div>
        ) : (
          <>
            <div
              css={css`
                width: 56px;
                height: 56px;
                border-radius: 50%;
                background-color: #edf7f2;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 8px;
              `}
            >
              <CallAllowIcon width={24} height={24} color="#3d906d" />
            </div>
            <QSTypography
              css={css`
                font-size: 16px;
                font-weight: 600;
                color: #2d2d2d;
                text-align: center;
                line-height: 140%;
              `}
            >
              Start {details?.numberOfTrialDays}-Day free trial?
            </QSTypography>

            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 2px;
              `}
            >
              <QSTypography
                css={css`
                  font-size: 14px;
                  font-weight: 500;
                  color: #7a7a7a;
                  text-align: center;
                `}
              >
                Available licenses - {details?.quantityApplicableForTrial}
              </QSTypography>
              {billingDate && (
                <QSTypography
                  css={css`
                    font-size: 14px;
                    font-weight: 500;
                    color: #7a7a7a;
                    text-align: center;
                  `}
                >
                  Next billing date - {dayjs(billingDate).format('Do MMM YYYY')}
                </QSTypography>
              )}
            </div>

            <div
              css={css`
                width: 100%;
                margin-top: 8px;
                border: 1px solid #e0e0e9;
                border-radius: 8px;
                display: flex;
                align-items: stretch;
              `}
            >
              <div
                css={css`
                  flex: 1;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  padding: 16px;
                  gap: 4px;
                `}
              >
                <QSTypography
                  css={css`
                    font-size: 13px;
                    font-weight: 400;
                    color: #7a7a7a;
                  `}
                >
                  Trial starts
                </QSTypography>
                <QSTypography
                  css={css`
                    font-size: 15px;
                    font-weight: 700;
                    color: #2d2d2d;
                  `}
                >
                  {dayjs(trialStartDate).format('Do MMM YYYY')}
                </QSTypography>
              </div>
              <div
                css={css`
                  flex: 1;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  padding: 16px;
                  gap: 4px;
                `}
              >
                <QSTypography
                  css={css`
                    font-size: 13px;
                    font-weight: 400;
                    color: #7a7a7a;
                  `}
                >
                  Trial end
                </QSTypography>
                <QSTypography
                  css={css`
                    font-size: 15px;
                    font-weight: 700;
                    color: #2d2d2d;
                  `}
                >
                  {trialEndDate ? dayjs(trialEndDate).format('Do MMM YYYY') : '—'}
                </QSTypography>
              </div>
            </div>

            <div
              css={css`
                margin-top: 4px;
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 2px;
              `}
            >
              <QSTypography
                css={css`
                  font-size: 12px;
                  font-weight: 400;
                  color: #7a7a7a;
                  text-align: center;
                `}
              >
                You can cancel the trial anytime.
              </QSTypography>
              <QSTypography
                css={css`
                  font-size: 12px;
                  font-weight: 400;
                  color: #7a7a7a;
                  text-align: center;
                `}
              >
                {currencySymbol}
                {priceDisplay} {billingPeriodLabel} after trial ends.
              </QSTypography>
            </div>
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions
        css={css`
          padding: 8px 12px;
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        `}
      >
        <QSButton
          css={css`
            height: 40px;
            background: #fff;
            box-shadow: none;
            color: #2d2d2d;
            border: none;
            font-weight: 500;
            font-size: 14px;
            :hover {
              background: #f5f5f5;
              box-shadow: none;
            }
          `}
          onClick={onClose}
          disabled={startTrialData.loading}
        >
          Cancel
        </QSButton>
        <QSButton
          css={css`
            height: 40px;
            min-width: 100px;
            font-weight: 600;
            font-size: 14px;
          `}
          onClick={onStartTrial}
          disabled={trialDetails.loading || startTrialData.loading || !!trialDetails.error}
        >
          {startTrialData.loading ? <Loader size={16} loaderProps={{ sx: { color: 'white' } }} /> : 'Start trial'}
        </QSButton>
      </DialogActions>
    </Dialog>
  );
}
